import RegistrationContent from "@/components/auth/registration/Content.tsx";
import RegistrationHeader from "@/components/auth/registration/Header.tsx";
import { RegisterUserInput } from "@/types";
import OutlinedPasswordInput from "@edusogno/inputs/outlined-password-input";
import AuthPageFooter from "@edusogno/layouts/auth-page-footer.js";
import { cn } from "@edusogno/lib/utils";
import LoadingButton from "@edusogno/primitives/loading-button";
import { enqueueSnackbar } from "notistack";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import RegistrationContainer from "../Container";

interface PasswordProps {
  className?: string;
  onNext: () => void;
}

const RegistrationPassword = ({ className, onNext }: PasswordProps) => {
  const { getFieldState, register, getValues } =
    useFormContext<RegisterUserInput>();

  const { t } = useTranslation();
  const passwordFieldState = getFieldState("password");
  const confirmFieldState = getFieldState("confirm_password");

  const errorPassword =
    passwordFieldState.isTouched && passwordFieldState.invalid;
  const confirmErrorPassword =
    confirmFieldState.isTouched && confirmFieldState.invalid;

  useEffect(() => {
    if (!passwordFieldState.isTouched && !confirmFieldState.isTouched) return;

    if (errorPassword || confirmErrorPassword) {
      enqueueSnackbar(
        passwordFieldState.error?.message || confirmFieldState.error?.message,
        {
          variant: "error",
          className: "w-full absolute right-0 bottom-0",
        },
      );
    }
     
  }, [
    errorPassword,
    confirmErrorPassword,
    confirmFieldState.error?.message,
    passwordFieldState.error?.message,
    passwordFieldState.isTouched,
    confirmFieldState.isTouched,
  ]);

  return (
    <>
      <RegistrationContainer className={cn("h-full", className)}>
        <RegistrationHeader
          title={t("auth.registration.password_title")}
          subtitle={t("auth.registration.password_subtitle")}
          logo={"password"}
        />

        <RegistrationContent>
          <OutlinedPasswordInput
            label={t("auth.labels.password")}
            color={"grey"}
            placeholder={"********"}
            autoComplete={"off"}
            {...register("password")}
            error={errorPassword}
          />
          <OutlinedPasswordInput
            label={t("auth.labels.confirm_password")}
            placeholder={"********"}
            autoComplete={"off"}
            color={"grey"}
            {...register("confirm_password")}
            error={confirmErrorPassword}
          />
        </RegistrationContent>
      </RegistrationContainer>
      <AuthPageFooter>
        <LoadingButton
          size={"lg"}
          type="button"
          className="hidden w-full text-lg tracking-wide md:block"
          disabled={
            errorPassword ||
            confirmErrorPassword ||
            passwordFieldState.invalid ||
            confirmFieldState.invalid
          }
          onClick={() => {
            const check =
              getValues("password") === getValues("confirm_password");
            if (!check) {
              enqueueSnackbar("Password does not match", {
                variant: "error",
                className: "w-full absolute right-0 bottom-0",
              });
            } else {
              onNext();
            }
          }}
        >
          {t("btns.next")}
        </LoadingButton>
        <LoadingButton
          size={"md"}
          type="button"
          className="block w-full text-lg tracking-wide md:hidden"
          disabled={errorPassword || confirmErrorPassword}
          onClick={() => {
            const check =
              getValues("password") === getValues("confirm_password");
            if (!check && getValues("password").length > 0) {
              enqueueSnackbar(confirmFieldState.error?.message, {});
              return;
            }
            onNext();
          }}
        >
          {t("btns.next")}
        </LoadingButton>
      </AuthPageFooter>
    </>
  );
};

export default RegistrationPassword;
